import { Text, Paper, Title, Flex, Button, Loader } from "@mantine/core";
import { createFileRoute, Link } from "@tanstack/react-router";
import "@mantine/dates/styles.css";
import { PolicyRecommendations } from "../components/policyRecommendations";
import {
  splitPolicies,
  toFundSwitchPolicies,
} from "../components/policyRecommendations/policies";

import { useGetCalculationById } from "../hooks/api/useGetCalculationById";
import PensionOverviewStack from "../components/pensionOverviewStack";
import { useUpdateCustomerData } from "../hooks/ui";
import { isFullCredentials, CustomerData } from "../hooks/ui/customerDataStore";
import { useGetAdviceById, useGetTransferPolicy } from "../hooks/api";
import { FooterButtons } from "../components/footerButtons";
import { isMobileDevice } from "../utils/useDeviceDetection";
import PoaExpiredBanner from "../components/poaValidation";

export const Route = createFileRoute("/pension-overview")({
  component: PensionOverview,
});

function PensionOverview() {
  const { customerData } = useUpdateCustomerData() as {
    customerData: CustomerData;
  };

  const { data, isFetching, isSuccess } = useGetCalculationById({
    credentials: customerData.credentials,
    enabled: isFullCredentials(customerData.credentials),
  });

  const { data: advice, isLoading } = useGetAdviceById({
    credentials: customerData.credentials,
    enabled: isFullCredentials(customerData.credentials),
  });

  const { data: transferPolicies } = useGetTransferPolicy({
    credentials: customerData.credentials,
    enabled: isFullCredentials(customerData.credentials),
  });

  const { transfers, others } = splitPolicies(data, [
    ...(Array.isArray(transferPolicies) ? transferPolicies : []),
    ...(advice?.insuranceAdvices ?? []),
  ]);

  return (
    <>
      <PoaExpiredBanner />
      <Title ff={"var(--wallet-font-family-sambla-sans)"} mt={"xl"} order={1}>
        Det här är din pension
      </Title>
      <Text mb={"xl"}>
        Vi har hämtat och sammanställt information om din pension så som den ser
        ut idag.
      </Text>
      <Paper px={"lg"} py={"md"} radius={"lg"} mb={"xl"}>
        {isFetching && (
          <Flex justify={"center"} align={"center"} mih={"20vh"}>
            <Loader />
          </Flex>
        )}
        {isSuccess && !isFetching && <PensionOverviewStack data={data} />}
      </Paper>
      {isFetching && (
        <Paper p={"xl"} radius={"lg"} mb={"xl"}>
          <Flex justify={"center"} align={"center"} mih={"20vh"}>
            <Loader />
          </Flex>
        </Paper>
      )}
      {isSuccess && !isFetching && (
        <PolicyRecommendations
          fundSwitches={toFundSwitchPolicies(data)}
          transfers={transfers}
          others={others}
          adviceAge={data?.parameters?.zAge}
        />
      )}
      <FooterButtons isLoading={isLoading}>
        {!(advice?.hasAgreedAdvice && advice?.insuranceAdvices?.length > 0) && (
          <Button
            component={Link}
            to="/home"
            data-testid="back-button"
            variant="default"
          >
            Tillbaka
          </Button>
        )}
        {!!advice?.insuranceAdvices?.length && (
          <Button
            component={Link}
            to="/pension-forecast"
            data-testid="pension-forecast-button"
          >
            {isMobileDevice() ? "Se prognos" : "Se pensionsprognos"}
          </Button>
        )}
      </FooterButtons>
    </>
  );
}
